export type CeremonySessionFields = {
    sessionCode: string
    sessionName: string
    startDate: string
    endDate: string
}

export const CEREMONY_TIME_OVERRIDES: Record<string, { startDate: string; endDate: string }> = {
    CEROC: {
        startDate: '2026-07-23T20:00:00+01:00',
        endDate: '2026-07-23T22:59:00+01:00',
    },
    CERCC: {
        startDate: '2026-08-02T21:00:00+01:00',
        endDate: '2026-08-02T23:59:00+01:00',
    },
}

const CEREMONY_NAME_TO_CODE: Record<string, keyof typeof CEREMONY_TIME_OVERRIDES> = {
    'Opening Ceremony': 'CEROC',
    'Closing Ceremony': 'CERCC',
}

const parseMs = (value: string) => {
    const ms = Date.parse(value)
    return Number.isFinite(ms) ? ms : null
}

const resolveCeremonyCode = (session: CeremonySessionFields): keyof typeof CEREMONY_TIME_OVERRIDES | null => {
    if (session.sessionCode in CEREMONY_TIME_OVERRIDES) {
        return session.sessionCode as keyof typeof CEREMONY_TIME_OVERRIDES
    }

    return CEREMONY_NAME_TO_CODE[session.sessionName] ?? null
}

export const isCeremonySession = (session: CeremonySessionFields): boolean => {
    return resolveCeremonyCode(session) !== null
}

export const applyCeremonySessionOverride = <T extends CeremonySessionFields>(session: T): T => {
    const ceremonyCode = resolveCeremonyCode(session)
    if (!ceremonyCode) {
        return session
    }

    const startMs = parseMs(session.startDate)
    const endMs = parseMs(session.endDate)
    if (startMs !== null && endMs !== null && endMs > startMs) {
        return session
    }

    const override = CEREMONY_TIME_OVERRIDES[ceremonyCode]
    return {
        ...session,
        startDate: override.startDate,
        endDate: override.endDate,
    }
}

export const applyCeremonyTimeOverrides = <
    TSession extends CeremonySessionFields,
    TDay extends { sessions: TSession[] },
    TSchedule extends { days: TDay[] },
>(
    schedule: TSchedule,
): TSchedule => {
    return {
        ...schedule,
        days: schedule.days.map((day) => ({
            ...day,
            sessions: day.sessions.map((session) => applyCeremonySessionOverride(session)),
        })),
    }
}
